import { Category, QuestionItem } from '../types/form';
import { evaluateVisibility } from './formVisibility';

/**
 * Checks if a category has any visible questions (recursively checking subcategories)
 * @param category The category to check
 * @param formData The current state of form answers
 * @returns true if the category has at least one visible question, false otherwise
 */
export const hasVisibleQuestions = (
  category: Category,
  formData: Record<string, any>,
  visibleIds?: Set<number>
): boolean => {
  // Check direct questions in this category
  if (category.questions && category.questions.length > 0) {
    const hasVisibleDirectQuestions = category.questions.some((question) =>
      evaluateVisibility(question, formData, visibleIds)
    );
    if (hasVisibleDirectQuestions) {
      return true;
    }
  }

  // Recursively check subcategories
  if (category.subcategories && category.subcategories.length > 0) {
    return category.subcategories.some((subcategory) =>
      hasVisibleQuestions(subcategory, formData, visibleIds)
    );
  }

  // No questions and no subcategories, or all questions are hidden
  return false;
};

/**
 * Filters categories to only include those with visible questions
 * @param categories Array of categories to filter
 * @param formData The current state of form answers
 * @returns Filtered array of categories with visible questions
 */
export const filterVisibleCategories = (
  categories: Category[],
  formData: Record<string, any>,
  visibleIds?: Set<number>
): Category[] => {
  return categories
    .map((category) => {
      // Check if this category has visible questions
      if (!hasVisibleQuestions(category, formData, visibleIds)) {
        return null;
      }

      // If it has subcategories, filter them recursively
      if (category.subcategories && category.subcategories.length > 0) {
        const filteredSubcategories = filterVisibleCategories(
          category.subcategories,
          formData,
          visibleIds
        );
        
        // If all subcategories were filtered out, hide this category too
        if (filteredSubcategories.length === 0) {
          return null;
        }
        
        return {
          ...category,
          subcategories: filteredSubcategories,
        };
      }

      return category;
    })
    .filter((category): category is Category => category !== null);
};

/**
 * Ordered leaf sections that currently have at least one visible question.
 * Matches sidebar visibility and is the source of truth for Save & Next.
 *
 * A "leaf" is a category with no subcategories — the same unit StepperForm
 * renders in the main panel (parents with children are containers only).
 */
export const getVisibleLeafCategories = (
  categories: Category[],
  formData: Record<string, any>,
  visibleIds?: Set<number>
): Category[] => {
  const visibleTree = filterVisibleCategories(categories, formData, visibleIds);
  const leaves: Category[] = [];

  const walk = (cats: Category[]) => {
    for (const cat of cats) {
      if (cat.subcategories && cat.subcategories.length > 0) {
        walk(cat.subcategories);
      } else {
        leaves.push(cat);
      }
    }
  };

  walk(visibleTree);
  return leaves;
};
